import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  IconButton,
  Typography,
  styled,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-toastify";
import CInput from "../../../components/CInput";
import CPhoneInput from "../../../components/CPhoneInput";
import { createCustomer } from "../../../services/contractorService";

const PrimaryButton = styled(Button)(() => ({
  backgroundColor: "#4D6BF6",
  color: "#fff",
  textTransform: "none",
  fontWeight: 500,
  borderRadius: "10px",
  boxShadow: "none",
  padding: "8px 22px",
  "&:hover": { backgroundColor: "#3B5BDB", boxShadow: "none" },
  "&.Mui-disabled": {
    backgroundColor: "#A4B3FB",
    color: "#fff",
  },
}));

const SecondaryButton = styled(Button)(() => ({
  backgroundColor: "#fff",
  color: "#0D0D0E",
  textTransform: "none",
  fontWeight: 500,
  borderRadius: "10px",
  border: "1px solid #D7D8D9",
  boxShadow: "none",
  padding: "8px 22px",
  "&:hover": { backgroundColor: "#F9FAFB", boxShadow: "none" },
}));

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const CreateCustomerModal = ({ open, onClose, onSave, prefillName = "" }) => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phoneState, setPhoneState] = useState({
    pCode: "971",
    countryCode: "AE",
    phone: "",
  });
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      const parts = (prefillName || "").trim().split(/\s+/).filter(Boolean);
      setFirstName(parts[0] || "");
      setLastName(parts.length > 1 ? parts.slice(1).join(" ") : "");
      setPhoneState({ pCode: "971", countryCode: "AE", phone: "" });
      setEmail("");
      setEmailError("");
      setSubmitting(false);
    }
  }, [open, prefillName]);

  const handlePhoneChange = (e) => {
    const digits = (e.target.value || "").replace(/\D/g, "").slice(0, 10);
    setPhoneState((prev) => ({ ...prev, phone: digits }));
  };

  const handleEmailChange = (e) => {
    const v = e.target.value;
    setEmail(v);
    if (v && !emailRegex.test(v)) {
      setEmailError("Please enter a valid email address");
    } else {
      setEmailError("");
    }
  };

  const canSave =
    !!firstName.trim() &&
    !!email.trim() &&
    !emailError &&
    !!phoneState.phone &&
    !submitting;

  const handleSave = async () => {
    if (!canSave) return;
    const first = firstName.trim();
    const last = lastName.trim();
    const fullName = [first, last].filter(Boolean).join(" ");
    const payload = {
      first_name: first,
      last_name: last,
      email: email.trim(),
      phone_code: phoneState.pCode,
      phone_no: phoneState.phone,
      managed_by: "contractor",
    };
    setSubmitting(true);
    try {
      const res = await createCustomer(payload);
      if (res?.success === false) {
        toast.error(res?.message || "Failed to add customer");
        return;
      }
      const apiData = res?.data || {};
      const customerId =
        apiData.customer_id || apiData.id || res?.customer_id || 0;
      // POST /link-customer returns `client_id` — null when the customer
      // isn't a registered user. Proposal payload expects a number, so we
      // coerce null → 0 here and store it on the customer object so the
      // parent's `buildProposalPayload` can read it directly.
      const clientId =
        apiData.client_id == null ? 0 : apiData.client_id;
      onSave?.({
        id: customerId,
        client_id: clientId,
        first_name: first,
        last_name: last,
        username: fullName,
        customer_email: email.trim(),
        phone_code: phoneState.pCode,
        phone_no: phoneState.phone,
      });
      toast.success("Customer added");
      onClose?.();
    } catch (err) {
      toast.error(err?.message || "Failed to add customer");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      PaperProps={{
        sx: {
          width: 520,
          maxWidth: "92vw",
          borderRadius: "16px",
          padding: "24px 24px 20px",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2.5,
        }}
      >
        <Typography
          sx={{
            fontSize: 20,
            fontWeight: 700,
            color: "#0D0D0E",
            fontFamily: "Inter Tight",
          }}
        >
          Add Customer
        </Typography>
        <IconButton onClick={onClose} size="small" disabled={submitting}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Box
        sx={{
          mb: 2,
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "10px",
        }}
      >
        <CInput
          label="First Name"
          placeholder="First name"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
        />
        <CInput
          label="Last Name"
          placeholder="Last name"
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
        />
      </Box>

      <Box sx={{ mb: 2 }}>
        <CPhoneInput
          label="Contact Number"
          placeholder="Phone"
          state={phoneState}
          setState={setPhoneState}
          onChange={handlePhoneChange}
          inputProps={{
            inputMode: "numeric",
            maxLength: 10,
            pattern: "[0-9]*",
          }}
        />
      </Box>

      <Box sx={{ mb: 3 }}>
        <CInput
          label="Email"
          placeholder="email@example.com"
          value={email}
          onChange={handleEmailChange}
          error={!!emailError}
          helpertext={emailError}
          type="email"
        />
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 1.5,
        }}
      >
        <SecondaryButton onClick={onClose} disabled={submitting}>
          Cancel
        </SecondaryButton>
        <PrimaryButton onClick={handleSave} disabled={!canSave}>
          {submitting ? (
            <CircularProgress size={18} sx={{ color: "#fff" }} />
          ) : (
            "Save"
          )}
        </PrimaryButton>
      </Box>
    </Dialog>
  );
};

export default CreateCustomerModal;
